import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Form, Col } from 'react-bootstrap';
import {
    Button, Grid, Typography,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import Select from 'components/widgets/Select';
import ConfirmDialog from 'components/widgets/modal/ConfirmDialog';
import { SaveIcon } from 'components/icons';
import CompanySettingsStyles from 'styles/modules/settings/CompanySettingsStyles';
import useManheimSetting from './useManheimSetting';

const styles = (theme) => ({
    ...CompanySettingsStyles.listStyles(theme),
    importButton: {
        width: 'auto',
        minWidth: '220px',
        backgroundColor: '#59A9FF',
        '&:hover': {
            backgroundColor: '#4D91DB',
        },
    },
});

const getCount = (items) => (Array.isArray(items) ? items.length : 0);

const ManheimSetting = ({ canWrite, classes }) => {
    const {
        manheimClientId,
        manheimDefaultLot,
        loading,
        hasLoadedCompanySettings,
        availableLots,
        manheimSellerCustomField,
        manheimLocationStatusCustomField,
        customFields,
        manheimUserImport,
        companyCode,
        importDialogOpen,
        isImporting,
        importResult,
        importError,
        setManheimClientId,
        setManheimDefaultLot,
        handleSubmit,
        setManheimSellerCustomField,
        setManheimLocationStatusCustomField,
        handleImportVehicles,
        closeImportDialog,
    } = useManheimSetting(canWrite);

    const lotOptions = availableLots.map((option) => ({
        ...option,
        value: String(option.value),
        label: String(option.label ?? ''),
    }));
    const selectedLot = lotOptions.find((option) => (
        option.value === String(manheimDefaultLot)
        || option.label === String(manheimDefaultLot)
    ));
    const customFieldOptions = customFields.map((option) => ({
        ...option,
        value: String(option.value),
    }));
    const hasImportResult = Boolean(importResult);

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <Button
                    size="small"
                    variant="contained"
                    className={clsx(classes.button, classes.saveButton)}
                    startIcon={<SaveIcon />}
                    disabled={!canWrite || loading || !hasLoadedCompanySettings}
                    onClick={handleSubmit}
                >
                    Save
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    className={clsx(classes.button, classes.importButton)}
                    disabled={!canWrite || loading || isImporting || !manheimUserImport || !companyCode}
                    onClick={handleImportVehicles}
                >
                    Import Vehicles From Manheim
                </Button>
            </div>

            <Form onSubmit={(event) => event.preventDefault()}>
                <Grid container spacing={1} className={classes.box}>
                    <Grid item xs={12} sm={12} md={6} lg={4}>
                        <Form.Group as={Col} controlId="manheimClientId">
                            <Form.Label>Client Id</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter Client Id"
                                value={manheimClientId}
                                disabled={!canWrite || loading}
                                onChange={(event) => setManheimClientId(event.target.value)}
                            />
                        </Form.Group>

                        <Form.Group as={Col} controlId="manheimDefaultLot">
                            <Form.Label>Default Lot</Form.Label>
                            <Select
                                name="manheimDefaultLot"
                                value={selectedLot?.value ?? String(manheimDefaultLot)}
                                options={lotOptions}
                                placeholder="Select"
                                maxMenuHeight={140}
                                disabled={!canWrite || loading}
                                onChange={(_, value) => {
                                    const selectedOption = lotOptions.find((option) => option.value === value);
                                    setManheimDefaultLot(selectedOption?.label ?? value);
                                }}
                            />
                        </Form.Group>

                        <Form.Group as={Col} controlId="manheimSellerCustomField">
                            <Form.Label>Seller Custom Field</Form.Label>
                            <Select
                                name="manheimSellerCustomField"
                                value={String(manheimSellerCustomField)}
                                options={customFieldOptions}
                                placeholder="Select"
                                maxMenuHeight={140}
                                onChange={(_, value) => setManheimSellerCustomField(value)}
                                disabled={!canWrite || loading}
                            />
                        </Form.Group>

                        <Form.Group as={Col} controlId="manheimLocationStatusCustomField">
                            <Form.Label>Location Status Custom Field</Form.Label>
                            <Select
                                name="manheimLocationStatusCustomField"
                                value={String(manheimLocationStatusCustomField)}
                                options={customFieldOptions}
                                placeholder="Select"
                                maxMenuHeight={140}
                                onChange={(_, value) => setManheimLocationStatusCustomField(value)}
                                disabled={!canWrite || loading}
                            />
                        </Form.Group>
                    </Grid>
                </Grid>
            </Form>

            {!loading && !manheimUserImport && (
                <Typography color="error">
                    The Manheim import user is not configured. Please contact an administrator to set it up.
                </Typography>
            )}

            <ConfirmDialog
                open={importDialogOpen}
                loading={isImporting}
                title="Import Vehicles From Manheim"
                titlePrimary="Close"
                hiddenSecondaryButton
                onClickPrimary={closeImportDialog}
                onClose={closeImportDialog}
            >
                {importError && (
                    <Typography color="error" style={{ whiteSpace: 'pre-line' }}>
                        {importError}
                    </Typography>
                )}
                {hasImportResult && !importError && (
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1">Import completed</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography>
                                Created:
                                {getCount(importResult.created)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography>
                                Updated:
                                {getCount(importResult.updated)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography>
                                Duplicate:
                                {getCount(importResult.duplicate)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography>
                                Errors:
                                {getCount(importResult.errors)}
                            </Typography>
                        </Grid>
                    </Grid>
                )}
            </ConfirmDialog>
        </div>
    );
};

ManheimSetting.propTypes = {
    canWrite: PropTypes.bool.isRequired,
    classes: PropTypes.objectOf(PropTypes.string).isRequired,
};

export default withStyles(styles)(ManheimSetting);
